import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import Input from "components/v2/Input/Input";
import { ReactComponent as CloseIcon } from "assets/images/iconsV2/x-close.svg";
import { DropdownOption } from "../Dropdown/Dropdown";

interface InputDropdownProps {
  name?: string;
  value?: string | null;
  defaultValue?: string;
  placeholder?: string;
  onChange?: (value: string | number) => void;
  onSelect?: (value: any) => void;
  required?: boolean;
  registerOptions?: object;
  errorMessage?: string;
  shouldUnregister?: boolean;
  disabled?: boolean;
  width?: string | number;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  id?: string;
  label?: string;
  options: DropdownOption[];
  alignRight?: boolean;
  readOnly?: boolean; // When true, blocks manual text input but allows dropdown selection (dropdown-only mode)
}

/**
 * InputDropdown - simple input that calls onChange(value) after debounce (default 500ms).
 * Works with react-hook-form via `name` prop (Input handles Controller internally).
 */
export default function InputDropdown({
  name,
  value,
  defaultValue = "",
  placeholder = "",
  onChange,
  onSelect,
  required,
  registerOptions,
  errorMessage,
  shouldUnregister,
  disabled,
  width,
  icon,
  iconRight,
  id,
  label,
  options = [],
  alignRight,
  readOnly = false,
}: InputDropdownProps) {
  const [internalValue, setInternalValue] = useState<string | number>(value ?? defaultValue ?? "");

  const [isExpanded, setIsExpanded] = useState(false);
  const [optionsFiltered, setOptionsFiltered] = useState<DropdownOption[]>(options);

  const divRef = useRef<HTMLDivElement>(null);

  // Compute the display value from the current value prop
  // When value prop changes (e.g., from API), find the matching option and display its label
  const getDisplayValue = () => {
    // If user is actively typing (not readOnly), show what they're typing
    if (!readOnly && internalValue && internalValue !== "") {
      return internalValue;
    }
    
    // If value is empty, show placeholder
    if (value === "" || value === null || value === undefined) {
      return "";
    }
    
    // Find matching option from options array
    const matchingOption = options.find((opt) => String(opt.value) === String(value));
    if (matchingOption) {
      const label = React.isValidElement(matchingOption.label)
        ? (matchingOption.label.props.children as string)
        : (matchingOption.label as string);
      return label;
    }
    
    // For user typing (not matched to an option), return internal value
    return internalValue || "";
  };

  const displayValue = getDisplayValue();

  useEffect(() => {
    // When value prop changes and in readOnly mode, clear internalValue
    // so that getDisplayValue() shows the converted label from value prop
    if (readOnly && value !== undefined) {
      setInternalValue("");
    }
  }, [value, readOnly]);

  useEffect(() => {
    // Close dropdown on outside click
    function handleClickOutside(event: MouseEvent) {
      if (divRef.current && !divRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    handleChangeForFilterOnClient({ target: { value: displayValue } } as React.ChangeEvent<HTMLInputElement>);
  }, [options]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // When readOnly is true, block manual text input but still allow dropdown selection
    // This enables "dropdown-only" mode where users can only select from predefined options
    if (readOnly) return;
    if (onChange) onChange(e.target.value);
    handleChangeForFilterOnClient(e);
  };

  const handleChangeForFilterOnClient = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setInternalValue(next);
    const filtered = options.filter((option) => {
      const label = React.isValidElement(option.label) ? (option.label.props.children as string) : (option.label as string);
      return label.toLowerCase().includes(next.toLowerCase());
    });
    setOptionsFiltered(filtered);
  };

  const handleSelect = (e: React.MouseEvent<HTMLDivElement>, option: DropdownOption) => {
    if (e) e.stopPropagation();
    if (disabled) return; // Prevent selection when disabled
    const label = React.isValidElement(option.label) ? (option.label.props.children as string) : (option.label as string);
    setInternalValue(label);
    setIsExpanded(false);
    if (onSelect) onSelect(option.value);
  };

  const handleClear = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setInternalValue("");
    setOptionsFiltered(options); // Reset to show all options
    if (onSelect) onSelect("");
    if (onChange) onChange("");
  };

  const showClearIcon = readOnly && displayValue && displayValue !== "";

  // Don't pass name to Input when using controlled mode (value prop provided)
  // This prevents double registration with react-hook-form when used inside a Controller
  const shouldRegisterWithForm = name && value === undefined;

  const handleDropdownClick = () => {
    if (disabled) return;
    // In readOnly mode, reset filtered options to show all options when opening dropdown
    if (readOnly) {
      setOptionsFiltered(options);
    }
    setIsExpanded(true);
  };

  return (
    <div className="dropdownV2" ref={divRef} style={{ height: "fit-content" }}>
      <div 
        onClick={handleDropdownClick}
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      >
        <Input
          id={id}
          name={shouldRegisterWithForm ? name : undefined}
          placeholder={placeholder}
          value={displayValue}
          defaultValue={defaultValue}
          onChange={handleChange}
          required={required}
          registerOptions={registerOptions}
          errorMessage={errorMessage}
          shouldUnregister={shouldUnregister}
          disabled={disabled}
          readOnly={readOnly}
          width={width}
          icon={icon}
          iconRight={
            showClearIcon ? (
              <div
                onClick={handleClear}
                style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                title="Clear selection"
              >
                <CloseIcon />
              </div>
            ) : (
              iconRight
            )
          }
          label={label}
          type="text"
        />
      </div>
      {isExpanded && (
        <div className={`dropdown-menu expand-below ${alignRight ? "align-right" : ""}`} style={{ width: "100%" }}>
          {optionsFiltered.length > 0 ? (
            optionsFiltered.map((option, index) => (
              <div key={index} className="dropdown-menu-item text-md" onClick={(e) => handleSelect(e, option)}>
                <div className="dropdown-content">
                  <div>
                    <div>{option.label}</div>
                    <div className="text-tertiary text-xs">{option.sublabel}</div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="dropdown-menu-item text-md px-lg py-md">
              <FormattedMessage id="recent.noResultsFound" defaultMessage="No results found" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

InputDropdown.defaultProps = {
  name: null,
  value: undefined,
  defaultValue: null,
  placeholder: "",
  onChange: () => {},
  onSelect: () => {},
  required: false,
  registerOptions: {},
  errorMessage: "",
  shouldUnregister: false,
  disabled: false,
  width: "100%",
  icon: null,
  iconRight: null,
  id: undefined,
  label: undefined,
  alignRight: false,
  readOnly: false,
};
